import { ConfigProvider, theme as antTheme } from "antd";
import type { ReactNode } from "react";

import { useThemeStore } from "@/store/theme";

/**
 * antd under this app's colours.
 *
 * Its defaults are its own design system — a different blue, a different
 * radius, a different type — which beside our own controls reads as something
 * borrowed from another product. The few tokens that decide how an antd
 * surface looks are taken from the CSS variables the rest of the app is drawn
 * from, and the dark algorithm follows the app's own switch rather than the
 * system's.
 *
 * Its own file rather than the date picker's, because three different things
 * borrow from antd now — the calendar, the CSV grid's Spin, and the bottom
 * sheet every modal turns into on a phone — and a primitive as small as a
 * dialog should not pull a date library in behind it to ask what colour it is.
 */
export function AntThemed({ children }: { children: ReactNode }) {
  const dark = useThemeStore((state) => state.choice) === "dark";
  const read = (name: string, fallback: string) =>
    typeof document === "undefined"
      ? fallback
      : getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

  return (
    <ConfigProvider
      theme={{
        algorithm: dark ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: read("--accent", "#3d5afe"),
          colorBgContainer: read("--surface-raised", "#ffffff"),
          // The one shade every field nobody may type in wears; without it the
          // calendar came out in antd's own grey beside our greyed inputs.
          colorBgContainerDisabled: read("--surface-disabled", "#e2e6ea"),
          colorBgElevated: read("--surface-raised", "#ffffff"),
          colorText: read("--fg", "#1c1c21"),
          colorTextPlaceholder: read("--fg-subtle", "#75757c"),
          colorBorder: read("--line-strong", "#d3d3d8"),
          borderRadius: 10,
          controlHeightLG: 40,
          fontFamily: "inherit",
          // Both, because the field is a large control and antd sizes those
          // from `fontSizeLG` — which it derives as `fontSize + 2` and which
          // is what stood the date two points taller than every field above it.
          fontSize: 15,
          fontSizeLG: 15,
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}
